import { forwardRef, useEffect } from 'react'
import type { HTMLProps, PropsWithChildren } from 'react'
import { css } from '~/styled-system/css'
import type { WithCss } from '~/styled-system/types'
import { adjustOverlay } from '~/ui/overlay'

export const Dialog = forwardRef<
  HTMLDialogElement,
  PropsWithChildren<HTMLProps<HTMLDialogElement> & WithCss>
>(({ children, css: cssProps, ...props }, ref) => {
  useEffect(() => {
    if (ref === null || !('current' in ref) || ref.current === null) {
      return
    }
    const dialog = ref.current
    const observer = new MutationObserver((list) => {
      for (const mutation of list) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          adjustOverlay()
        }
      }
    })
    observer.observe(dialog, { attributes: true, attributeFilter: ['open'] })
    return () => {
      observer.disconnect()
    }
  }, [ref])

  return (
    <dialog
      ref={ref}
      className={css(
        {
          minWidth: '[325px]',
          maxWidth: '[480px]',
          // iOS 16 以下では `::backdrop` 疑似要素で custom property が継承されないので
          // modal.background.foreground token と同じ値を直接指定する
          // https://developer.chrome.com/blog/css-backdrop-inheritance
          _backdrop: { bgColor: '[rgb(0 0 0 / 0.6)]' },
        },
        // PandaCSS が確実に適用されるように css.raw 使って指定
        import.meta.env.VITE_MODE === 'ppg'
          ? css.raw({ borderRadius: 'xl' })
          : css.raw({ borderRadius: 'lg' }),
        cssProps
      )}
      {...props}
    >
      {children}
    </dialog>
  )
})
